import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth, roleHome } from "../../context/AuthContext";
import { Logo } from "../Logo";

const links = [
  { to: "/features", label: "Features" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function MarketingLayout() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#050505] text-gray-200">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center">
            <Logo size={50} mobileSize={40} />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}-link`}
                className={({ isActive }) => `text-sm transition-colors ${isActive ? "text-[#00F0FF]" : "text-gray-400 hover:text-white"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Link data-testid="nav-dashboard-link" to={roleHome(user.role)} className="btn-primary px-5 py-2 text-sm">Dashboard</Link>
            ) : (
              <>
                <Link data-testid="nav-login-link" to="/login" className="text-sm text-gray-300 hover:text-white px-3 py-2">Sign In</Link>
                <Link data-testid="nav-register-link" to="/register" className="btn-primary px-5 py-2 text-sm">Get Started</Link>
              </>
            )}
          </div>
          <button data-testid="mobile-menu-button" className="md:hidden flex items-center gap-2 text-gray-300" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {/* Mobile: always-visible auth actions so users never dig through the menu to sign in */}
        <div className="md:hidden border-t border-white/10 bg-[#0a0a0a]/60 px-4 py-2.5 flex items-center gap-2">
          {user ? (
            <Link data-testid="mobile-nav-dashboard-link" to={roleHome(user.role)} className="btn-primary flex-1 text-center px-4 py-2 text-xs">Dashboard</Link>
          ) : (
            <>
              <Link data-testid="mobile-nav-login-link" to="/login" className="flex-1 text-center text-xs text-gray-200 border border-white/15 rounded-full px-4 py-2 hover:bg-white/5">Sign In</Link>
              <Link data-testid="mobile-nav-register-link" to="/register" className="btn-primary flex-1 text-center px-4 py-2 text-xs">Get Started</Link>
            </>
          )}
        </div>
        {open && (
          <div className="md:hidden border-t border-white/10 bg-[#0a0a0a] px-6 py-4 space-y-3">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-sm text-gray-300">{l.label}</NavLink>
            ))}
            <Link to={user ? roleHome(user.role) : "/login"} onClick={() => setOpen(false)} className="block text-sm text-[#00F0FF]">
              {user ? "Dashboard" : "Sign In"}
            </Link>
          </div>
        )}
      </header>
      <main className="pt-[124px] md:pt-20"><Outlet /></main>
      <footer className="border-t border-white/10 mt-24">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div className="col-span-2 md:col-span-1">
            <Logo size={44} className="mb-3" />
            <p className="text-gray-500 text-xs leading-relaxed mt-3">Institutional-grade trade copier management &amp; analytics. Your funds never leave your broker.</p>
            <p className="text-gray-500 text-xs mt-2"><a href="mailto:support@chartzfx.com" className="hover:text-white">support@chartzfx.com</a></p>
          </div>
          <div>
            <p className="label-xs mb-3">Product</p>
            {links.slice(0, 2).map((l) => <Link key={l.to} to={l.to} className="block text-gray-400 hover:text-white py-1">{l.label}</Link>)}
          </div>
          <div>
            <p className="label-xs mb-3">Company</p>
            <Link to="/about" className="block text-gray-400 hover:text-white py-1">About</Link>
            <Link to="/contact" className="block text-gray-400 hover:text-white py-1">Contact</Link>
          </div>
          <div>
            <p className="label-xs mb-3">Legal</p>
            <Link data-testid="footer-privacy-link" to="/privacy" className="block text-gray-400 hover:text-white py-1">Privacy Policy</Link>
            <Link data-testid="footer-terms-link" to="/terms" className="block text-gray-400 hover:text-white py-1">Terms &amp; Conditions</Link>
            <Link data-testid="footer-refund-link" to="/refund-policy" className="block text-gray-400 hover:text-white py-1">Refund Policy</Link>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-gray-600 px-6">
          © {new Date().getFullYear()} ChartzFx — a management &amp; analytics platform. Not a broker, exchange or custodian. Trading involves risk.
        </div>
      </footer>
    </div>
  );
}
